import React, { useRef, useEffect, useState, useCallback } from "react";

interface ResizableColumnHeaderProps {
  width: number;
  onResize: (width: number) => void;
  children: React.ReactNode;
  minWidth?: number;
  maxWidth?: number;
}

const ResizableColumnHeader: React.FC<ResizableColumnHeaderProps> = ({
  width,
  onResize,
  children,
  minWidth = 50,
  maxWidth = 800,
}) => {
  const [isResizing, setIsResizing] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const startXRef = useRef<number>(0);
  const startWidthRef = useRef<number>(width);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsResizing(true);
      startXRef.current = e.clientX;
      startWidthRef.current = width;
    },
    [width],
  );

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const diff = e.clientX - startXRef.current;
      let newWidth = startWidthRef.current + diff;
      // Apply min/max constraints
      newWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));
      onResize(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing, minWidth, maxWidth, onResize]);

  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        height: "100%",
        userSelect: isResizing ? "none" : "auto",
      }}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => !isResizing && setIsHovering(false)}
    >
      <div style={{ flex: 1, overflow: "hidden" }}>{children}</div>
      <div
        data-resize-handle="true"
        style={{
          position: "absolute",
          right: 0,
          top: 0,
          bottom: 0,
          width: 12,
          cursor: "col-resize",
          zIndex: 10,
          backgroundColor:
            isResizing || isHovering
              ? "rgba(90, 172, 250, 0.22)"
              : "rgba(24, 144, 255, 0.10)",
          borderRight: "2px solid #98c9f8",
          transition: "background-color 0.2s, border-color 0.2s",
        }}
        onMouseDown={handleMouseDown}
        onDragStart={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      />
    </div>
  );
};

export default ResizableColumnHeader;
